import { useState } from "react";
import { timelineData } from "@/mocks/gossip";

const toneDot: Record<string, string> = {
  primary: "bg-primary-500 ring-primary-100",
  accent: "bg-accent-500 ring-accent-100",
  secondary: "bg-secondary-500 ring-secondary-100",
};

const toneText: Record<string, string> = {
  primary: "text-primary-700",
  accent: "text-accent-700",
  secondary: "text-secondary-900",
};

const toneDate: Record<string, string> = {
  primary: "text-primary-700",
  accent: "text-accent-700",
  secondary: "text-secondary-700",
};

export default function Timeline() {
  const [era, setEra] = useState("all");
  const current = timelineData.find((t) => t.era === era) ?? timelineData[0];

  return (
    <section id="timeline" className="paper-grain w-full py-14 md:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
          <div className="flex flex-col gap-2 border-l-4 border-primary-500 pl-4">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-secondary-600">考古时间线 · Timeline</p>
            <h2 className="font-heading text-3xl font-black text-foreground-950 md:text-4xl">吃瓜八卦历史时间线</h2>
            <p className="max-w-2xl text-sm text-foreground-600">
              把同一个事件的碎片按年月归档，一条一条还原来龙去脉，正是旧料翻旧账的正确姿势。
            </p>
          </div>

          {/* 年代切换 */}
          <div className="inline-flex items-center gap-1 rounded-full border border-background-300/70 bg-background-50 p-1">
            {timelineData.map((t) => (
              <button
                key={t.era}
                onClick={() => setEra(t.era)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  era === t.era ? "bg-foreground-900 text-background-50" : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* 时间线主体 */}
        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1.2fr_1fr]">
          {/* 左：竖向时间线 */}
          <div className="relative">
            <div className="absolute bottom-2 left-[7px] top-2 w-px bg-foreground-900/15" />
            <ol className="space-y-7">
              {current.points.map((p, i) => (
                <li key={p.date} className="relative flex gap-5 pl-8">
                  <span
                    className={`absolute left-0 top-1 flex h-[15px] w-[15px] items-center justify-center rounded-full ring-4 ${toneDot[p.tone]}`}
                  />
                  <div className="flex-1 rounded-lg border border-background-300/60 bg-background-50 p-4 md:p-5">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`font-display text-lg ${toneDate[p.tone]}`}>
                        {p.date}
                      </span>
                      <span className={`text-sm font-semibold ${toneText[p.tone]}`}>{p.title}</span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-700">{p.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* 右：方法说明 */}
          <aside className="flex flex-col gap-4">
            <div className="rounded-lg bg-foreground-900 p-6 text-background-100">
              <h3 className="font-heading text-xl font-bold text-background-50">我们怎么「翻旧账」？</h3>
              <ol className="mt-4 space-y-3">
                {[
                  ["01", "线索打捞", "跨平台检索同一事件在历年留下的公开痕迹。"],
                  ["02", "交叉比对", "用不同来源互相验证时间与细节，剔除以讹传讹。"],
                  ["03", "时间归档", "按年月日排序，拼出完整事件发酵曲线。"],
                ].map(([n, t, d]) => (
                  <li key={n} className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-500 font-heading text-xs font-bold text-background-50">
                      {n}
                    </span>
                    <p className="text-sm leading-relaxed">
                      <span className="font-semibold text-background-50">{t}：</span>
                      <span className="text-background-200">{d}</span>
                    </p>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rounded-lg border border-background-300/60 bg-background-50 p-6">
              <h4 className="flex items-center gap-2 text-sm font-bold text-foreground-900">
                <i className="ri-file-history-line text-secondary-600" />
                旧料进度提醒
              </h4>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                当某个旧话题被重新提起、或迎来周年节点，我们都会第一时间补全对应的
                <strong className="font-semibold text-foreground-900">陈年往事档案</strong>。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}